import React, { useContext } from 'react';
import Table, { HeaderRow, TableRow } from '../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { Link, useNavigate } from 'react-router-dom';
import { SiteContext } from '../../../context/SiteContext';
import { PageAbsolutePath } from '../../../consts/Common';
import Links from '../../common/Widgets/Links';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

const AccidentAssistanceAncillaryPage = (): JSX.Element => {
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayName, brandDisplayFullName, brandPhoneNumbers } = brand;

  const phoneNumber = brandPhoneNumbers.claimsNumber;

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const columnwithsup = (
    <p>
      {brandDisplayName} manufacturer warranty & safety rating preserved
      <sup>1</sup>
    </p>
  );

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const tableHeader: HeaderRow = {
    columns: [
      `Guaranteed benefits`,
      `${brandDisplayFullName} Accident Assistance`,
      'Top UK insurers'
    ]
  };

  const tableRows: TableRow[] = [
    {
      columns: [
        `${brandDisplayName} Genuine parts`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [
        `${brandDisplayName} Approved repairers`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [
        `${brandDisplayName} trained technicians`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [columnwithsup, tickComponent, crossComponent]
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  const goBack = (): void => {
    navigate(-1);
  };

  return (
    <div className='accident-asistance'>
      <Links
        id='back-link'
        title=''
        items={[{ to: '', text: 'Back', onClick: goBack }]}
        icon={chevronLeftComponent}
      />
      <h2>{brandDisplayName} Accident Assistance</h2>
      <p>
        Accidents happen. But with complimentary {brandDisplayName} Accident
        Assistance, you can leave it to us to take care of things and get your{' '}
        {brandDisplayName} back on the road. We're here for you, 24 hours a day,
        whoever you're insured with.
      </p>
      <p>
        Hassle. Inconvenience. Things you don't want after an accident. So just
        call {brandDisplayName} first on{' '}
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>{' '}
        before your insurer, and we'll do the rest. We'll look after your
        insurance claim on your behalf, provide accident recovery and a free
        replacement car while your {brandDisplayName} is being repaired, all at
        no cost to you.
      </p>
      <p>
        And you can expect the highest quality repair at a {brandDisplayName}{' '}
        Approved Repairer where only {brandDisplayName} Genuine Parts will be
        used - something that insurers don't always guarantee.
      </p>
      <h2>We take better care of your {brandDisplayName}</h2>
      <Table
        id='landing-page-table'
        header={tableHeader}
        rows={tableRows}
        addendum='Research: UK motor insurers’ policy wordings April 2023.'
      />
      <p>
        Accident? Call {brandDisplayName} first on{' '}
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
        . Save the number in your phone, just in case.
      </p>
      <p>
        <sup>1</sup>By fitting genuine parts. If your insurer uses non-genuine
        parts which subsequently fail, they will not be covered under your
        manufacturer's warranty.
      </p>
      <p>Conditions and exclusions:</p>
      <ul>
        <li>
          The {brandDisplayName} Accident Assistance service is available in the
          UK when you call {brandDisplayName} first on{' '}
          <a className='calltext' href={`tel:${phoneNumber}`}>
            {phoneNumber}
          </a>{' '}
          following an accident, before your insurer, providing that we handle
          your claim on your behalf with your insurer and manage your repair
          through a {brandDisplayName} Approved Repairer.
        </li>
        <li>
          The service is not available for Motability vehicles or misfuelling
          claims. For standalone glass claims, we can refer you to our glass
          provider, but this will be subject to the terms and conditions of your
          own insurance policy.
        </li>
        <li>
          Accident recovery is provided for unroadworthy vehicles only. If you
          cancel the recovery of your vehicle, any cancellation charges incurred
          will be at your cost. If your insurer rejects your claim, any recovery
          costs incurred will be at your cost.
        </li>
        <li>
          A replacement car is provided subject to availability for accident
          repairs (excluding standalone glass repairs/replacements) carried out
          by a {brandDisplayName} Approved Repairer. We will provide a repairer
          courtesy car if the incident is your fault, or if the incident is not
          your fault, we will aim to provide a hire car of a similar size to
          your own vehicle.
        </li>
        <li>
          In some instances where your insurer may not let us report the claim
          to them on your behalf, we will ask you to report to them advising
          that you are using our {brandDisplayName} Accident Assistance service
          with a {brandDisplayName} Approved Repairer.
        </li>
        <li>
          Some insurers may charge an additional excess for using our repairers.
        </li>
        <li>
          Following an accident, we can arrange for you and your passengers to
          be taken home or to an address of your choice (including a railway
          station or such like for onward travel) where such home or address is
          within a 20 mile radius of the incident site.
        </li>
      </ul>
      <p>
        {brandDisplayName} Accident Assistance is provided by Car Care Plan
        Limited which is registered in England, Company No. 850195 at Jubilee
        House, 5 Midpoint Business Park, Thornbury, West Yorkshire, BD3 7AG. To
        understand how we may process your information read our
        <Link
          id='lnkFairUseNotice'
          className='in-text'
          to={PageAbsolutePath.Insurance.FairUseNoticePage}>
          Fair Use Notice
        </Link>
      </p>
      <div className='flex-row'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
      </div>
    </div>
  );
};

export default AccidentAssistanceAncillaryPage;
